import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Divider,
  IconButton,
  Grid,
  Autocomplete,
  Menu,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { toast } from "react-toastify";
import { exportFinanceDashboardApi } from "../services/reports.service";

import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import { ArrowLeft } from "lucide-react";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers";
import { EditIcon, ShareIcon } from "../comps/CustomIcons";

import ReportsLiveChart from "./ReportsLiveChart";
import ReportsBedOccupancyTable from "./ReportsBedOccupancyTable";
import ReportsFinanceDashboardTable from "./ReportsFinanceDashboardTable";

const iconStyleProps = {
  backgroundColor: "rgba(240, 247, 252, 1)",
  width: "44px",
  height: "44px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
};

// Proper dropdown options for Finance Dashboard
const templateOptions = [
  { label: "All Categories", value: "all_categories" },
  { label: "IPD Expenses", value: "ipd_expenses" },
  { label: "OPD Expenses", value: "opd_expenses" },
  { label: "Pharmacy Expenses", value: "pharmacy_expenses" },
  { label: "Laboratory Expenses", value: "laboratory_expenses" },
  { label: "Radiology Expenses", value: "radiology_expenses" },
  { label: "Surgery Expenses", value: "surgery_expenses" },
  { label: "Emergency Expenses", value: "emergency_expenses" },
];

// Sample chart data for Finance Dashboard
const financeChartData = [
  { name: 'IPD', value: 450000, color: '#8884d8' },
  { name: 'OPD', value: 320000, color: '#82ca9d' },
  { name: 'Pharmacy', value: 280000, color: '#ffc658' },
  { name: 'Laboratory', value: 180000, color: '#ff7300' },
  { name: 'Radiology', value: 150000, color: '#e91e63' },
  { name: 'Surgery', value: 220000, color: '#9c27b0' },
];

export default function ReportsFinanceDashboard() {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    searchTemplate: [],
    date: null,
  });

  const [tableData, setTableData] = useState([]);
  const [chartData, setChartData] = useState(financeChartData);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errors, setErrors] = useState([]); // For required field validation errors
  
  const handleBack = () => {
    navigate(-1);
  };

  const [activeTab, setActiveTab] = useState(1);
  const handleTabClick = (index) => {
    setActiveTab(index);
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    // Validate required fields and collect error field names
    const errorFields = [];
    if (!formData.searchTemplate || formData.searchTemplate.length === 0) {
      errorFields.push('searchTemplate');
    }
    if (!formData.date) {
      errorFields.push('date');
    }
    
    if (errorFields.length > 0) {
      setErrors(errorFields);
      toast.error("Please fill all the required fields.");
      return;
    }
    
    // Clear errors if validation passes
    setErrors([]);
    
    setIsLoading(true);
    
    try {
      // Simulate API call - replace with actual API endpoint
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock data for demonstration - replace with actual API response
      const mockTableData = [
        {
          _id: "1",
          category: "Anesthesia",
          ipdExpense: "₹ 1,12,00",
          ipdSubCategory: "Anesthesia Charge",
          ipdExpenseAmount: "₹ 1,12,00",
          opdExpense: "₹ 0",
          opdSubCategory: "Anesthesia Charge", 
          opdExpenseAmount: "₹ 0"
        },
        {
          _id: "2",
          category: "Surgery",
          ipdExpense: "₹ 2,25,00",
          ipdSubCategory: "Surgery Charge",
          ipdExpenseAmount: "₹ 2,25,00",
          opdExpense: "₹ 50,00",
          opdSubCategory: "Minor Surgery",
          opdExpenseAmount: "₹ 50,00"
        },
        {
          _id: "3",
          category: "Medication",
          ipdExpense: "₹ 1,85,00",
          ipdSubCategory: "Medicine Charge",
          ipdExpenseAmount: "₹ 1,85,00",
          opdExpense: "₹ 75,00",
          opdSubCategory: "Prescription Medicine",
          opdExpenseAmount: "₹ 75,00"
        }
      ];
      
      setTableData(mockTableData);
      
      // Update chart data based on search criteria
      const updatedChartData = financeChartData.map(item => ({
        ...item,
        value: Math.floor(Math.random() * 500000) + 100000 // Random values for demo
      }));
      setChartData(updatedChartData);
      
      toast.success("Finance dashboard data loaded successfully");
    } catch (error) {
      console.error("Error fetching finance dashboard data:", error);
      toast.error("Failed to load finance dashboard data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    if (tableData.length === 0 && chartData.length === 0) {
      toast.warning("Please search for data before exporting");
      return;
    }
    
    setIsExporting(true);
    
    try {
      // Prepare export parameters
      const exportParams = {
        startDate: formData.startDate?.toISOString(),
        endDate: formData.endDate?.toISOString(),
        page: 1,
        limit: 1000 // Export all records
      };

      // Remove undefined/empty values
      Object.keys(exportParams).forEach(key => {
        if (exportParams[key] === undefined || exportParams[key] === '' || exportParams[key] === null) {
          delete exportParams[key];
        }
      });

      const result = await exportFinanceDashboardApi(exportParams);
      
      if (result.success) {
        toast.success(`Successfully exported ${result.count} records to Excel!`);
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      console.error("Error exporting report:", error);
      toast.error(`Export failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px" }}>
          {/* Search and Buttons */}
          <Box sx={{ my: "20px" }}>
            <Button
              startIcon={<ArrowLeft />}
              onClick={handleBack}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
              }}
            >
              Back
            </Button>
            <Typography sx={{
              fontFamily: "Open Sans",
              fontWeight: 600,
              fontSize: "20px",
              lineHeight: "29px",
              letterSpacing: "0px",
              color: "#5D6A82",
              marginBottom: "1rem",
            }}>
              Finance Dashboard - Total Category Wise Expense
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              my: "20px",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={templateOptions || []}
                  multiple
                  limitTags={2}
                  value={formData.searchTemplate}
                  onChange={(event, newValue) => {
                    handleInputChange('searchTemplate', newValue);
                    // Clear error when field is changed
                    if (errors.includes('searchTemplate')) {
                      setErrors(errors.filter(e => e !== 'searchTemplate'));
                    }
                  }}
                  getOptionLabel={(option) => option?.label || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Template"
                      name="searchTemplate"
                      required
                      InputLabelProps={{
                        required: true,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('searchTemplate') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  name="date"
                  label="Date"
                  value={formData.date}
                  onChange={(newValue) => {
                    handleInputChange('date', newValue);
                    // Clear error when field is changed
                    if (errors.includes('date')) {
                      setErrors(errors.filter(e => e !== 'date'));
                    }
                  }}
                  slotProps={{
                    textField: { 
                      required: true, 
                      fullWidth: true,
                      InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }
                    },
                  }}
                  sx={{ 
                    bgcolor: "white",
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: errors?.includes('date') ? '1px solid red' : '1px solid inherit',
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: "20px",
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={handleExport}
              disabled={isExporting || (tableData.length === 0 && chartData.length === 0)}
              sx={{ minWidth: 120 }}
            >
              {isExporting ? <CircularProgress size={20} /> : 'Export'}
            </Button>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={isLoading}
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Search'}
            </Button>
          </Box>

          <Box
            sx={{
              // border: "1px solid rgba(223, 232, 239, 1)",
              borderRadius: "8px",
              mb: "30px",
              p: "20px",
            }}
          >
            {/* dynamic button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  border: "1px solid grey",
                  borderRadius: "5px",
                  px: 2,
                  py: 0.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  width: "280px",
                  cursor: "pointer",
                  mb: "20px",
                }}
              >
                <Typography
                  onClick={() => handleTabClick(1)}
                  sx={{
                    color: "grey",
                    textDecoration: "none",
                    ...(activeTab === 1 && {
                      color: "primary.main",
                      fontWeight: 600,
                      textDecoration: "underline",
                      textUnderlineOffset: "4px",
                    }),
                  }}
                >
                  Table
                </Typography>
                <Divider flexItem orientation="vertical" />
                <Typography
                  onClick={() => handleTabClick(2)}
                  sx={{
                    color: "grey",
                    textDecoration: "none",
                    ...(activeTab === 2 && {
                      color: "primary.main",
                      fontWeight: 600,
                      textDecoration: "underline",
                      textUnderlineOffset: "4px",
                    }),
                  }}
                >
                  Stacked Bar Chart
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1,
                  mb: "20px",
                }}
              >
                {activeTab === 1 && tableData.length > 0 && (
                  <Typography sx={{ fontSize: "14px" }}>
                    Rows: {tableData.length} | Column: 8
                  </Typography>
                )}
                {activeTab === 1 && tableData.length > 0 && (
                  <Box sx={iconStyleProps}>
                    <EditIcon
                      sx={{
                        cursor: "pointer",
                        color: "transparent",
                        fontSize: "18px",
                      }}
                    />
                  </Box>
                )}
                <Box sx={iconStyleProps}>
                  <ShareIcon />
                </Box>
                <Box sx={iconStyleProps}>
                  <IconButton>
                    <FileDownloadOutlinedIcon />
                  </IconButton>
                </Box>
              </Box>
            </Box>

            {activeTab === 1 && tableData.length > 0 && <ReportsFinanceDashboardTable tableData={tableData} />}
            {activeTab === 1 && tableData.length === 0 && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: 200,
                border: '2px dashed #ccc',
                borderRadius: 2
              }}>
                <Typography variant="h6" color="text.secondary">
                  No data available. Please search to load finance data.
                </Typography>
              </Box>
            )}
            {activeTab === 2 && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                pt: 2 
              }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Category Wise Expense Distribution
                </Typography>
                {chartData.length > 0 ? (
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: 2,
                    width: '100%',
                    maxWidth: 1000
                  }}>
                    {chartData.map((item, index) => (
                      <Box key={index} sx={{
                        backgroundColor: item.color + '20',
                        borderRadius: 2,
                        p: 2,
                        textAlign: 'center',
                        border: `2px solid ${item.color}`,
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'scale(1.05)'
                        }
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: item.color }}>
                          {item.name}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                          ₹{(item.value / 1000).toFixed(0)}K
                        </Typography>
                        <Box sx={{
                          width: '100%',
                          height: 8,
                          backgroundColor: '#f0f0f0',
                          borderRadius: 4,
                          mt: 2,
                          overflow: 'hidden'
                        }}>
                          <Box sx={{
                            width: `${Math.min((item.value / Math.max(...chartData.map(d => d.value))) * 100, 100)}%`,
                            height: '100%',
                            backgroundColor: item.color,
                            borderRadius: 4,
                            transition: 'width 1s ease-in-out'
                          }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    minHeight: 200,
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    width: '100%'
                  }}>
                    <Typography variant="h6" color="text.secondary">
                      No chart data available. Please search to load finance data.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
